// frontend/src/api/axiosInstance.js
// Project:      Vision ANPR - Global Axios instance
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-20
// Updated:      2025-08-13
//
// Description:  Centralized Axios instance with JWT injection, duplicate '/api' guard, and 401 handling.

import axios from 'axios';

const RAW_BASE = process.env.REACT_APP_API_BASE || '/api';

// Normalize base (no trailing slash)
const BASE = RAW_BASE.replace(/\/+$/, '');

const axiosInstance = axios.create({
  baseURL: BASE,
  timeout: 15000,
});

// ---- Request Interceptor ----
axiosInstance.interceptors.request.use(
  (config) => {
    // Inject JWT (store bare token or with 'Bearer ' — we normalize)
    const raw = localStorage.getItem('anpr_token');
    if (raw) {
      const jwt = raw.replace(/^Bearer\s+/i, '');
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${jwt}`;
    }

    // If url is relative, prevent '/api/api/...'
    const isAbsolute = /^https?:\/\//i.test(config.url || '');
    if (!isAbsolute) {
      // Strip any leading '/api' (one or more) and extra slashes
      const baseRe = new RegExp(`^(?:${BASE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?:/)?`, 'i');
      let u = (config.url || '')
        .replace(baseRe, '')  // drop leading base if caller included it
        .replace(/^\/+/, ''); // drop stray leading slashes

      // Axios will join baseURL + url -> '/api' + '/' + 'users' = '/api/users'
      config.url = u;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Response Interceptor ----
axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      console.warn('Unauthorized (401).');
      // optional: kick to login or clear token here
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
